// DynamicRulesEditor — the DYNAMIC (rule-engine) billing builder for the register
// form. Edits endpoints.${index}.dynamicRules as UI-state (see ../dynamic-rules.js);
// register's transformEndpointToApi compiles it to the engine config on save.
//
// Visual follows docs/plan/dynamic-billing-register-tab.html (#72): a table field
// pool, and each charge item rendered as one composite box `[字段▾ × 单价]` joined by
// operator boxes. Two switchable layouts — 横排 (wraps) / 竖排堆叠 (one item per row).
// Per-rule numeric preview (sample values → $) is illustrative; the backend validator
// + the live engine stay authoritative.
//
// Dropdowns use the project's base-ui <Select> wrapper; text inputs / buttons stay raw.

import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { XApi } from '../../services.js';
import { Select, Dialog, DialogHeader, DialogBody } from '../../ui/index.js';
import { XIcons } from '../../primitives.jsx';

// Syntax-highlighted JSON editor (rehype/refractor, ~300KB) — lazy so it only loads when
// the provider switches to JSON mode.
const CodeEditor = React.lazy(() => import('@uiw/react-textarea-code-editor'));
import {
  ROOTS,
  ROOTLABEL,
  OPS,
  FIELD_TYPES,
  isResp,
  numFieldsOf,
  mapFieldsOf,
  newField,
  newFactor,
  newItem,
  newRule,
  EMPTY_DYNAMIC_UI,
  builtExpr,
  compileRules,
  deserializeRules,
  previewStr,
} from '../dynamic-rules.js';

const TYPE_COLOR = { number: '#52d3ad', string: '#6aa6ff', map: '#c79bff' };
const GREEN = '#52d3ad';
// 项间连接运算符:点击循环切换(避免 base-ui 弹窗那种带勾选的竖列,显得怪)。
const OP_CYCLE = ['+', '-', '*', '/'];
const OP_SYM = { '+': '+', '-': '−', '*': '×', '/': '÷' };

const S = {
  pill: (kind) => ({
    display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600,
    color: kind === 'ok' ? 'var(--ok, #52d3ad)' : 'var(--err, #ff7a7a)',
    background: kind === 'ok' ? 'rgba(82,211,173,.16)' : 'rgba(255,122,122,.16)',
  }),
  card: { background: 'var(--bg-1)', border: '1px solid var(--line)', borderRadius: 12, padding: 14 },
  secHead: { display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0 11px' },
  secNum: {
    width: 20, height: 20, borderRadius: '50%', background: 'var(--bg-2)', border: '1px solid var(--line)',
    display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 700, color: GREEN,
  },
  secTitle: { fontSize: 13, fontWeight: 700, color: 'var(--fg-1)' },
  hint: { fontSize: 11.5, color: 'var(--fg-3)', margin: 0, lineHeight: 1.5 },
  ghost: { background: 'transparent', border: '1px solid var(--line)', borderRadius: 8, padding: '4px 11px', fontSize: 12, cursor: 'pointer', color: 'var(--fg-2)' },
  // ── field pool table ──
  tbl: { border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-2)' },
  th: { display: 'grid', gridTemplateColumns: '150px 130px 1fr 116px 56px', gap: 9, padding: '8px 12px', background: 'var(--bg-1)', borderBottom: '1px solid var(--line)', fontSize: 10.5, color: 'var(--fg-4)', textTransform: 'uppercase', letterSpacing: '.4px' },
  tr: { display: 'grid', gridTemplateColumns: '150px 130px 1fr 116px 56px', gap: 9, padding: '7px 12px', alignItems: 'center', borderBottom: '1px solid var(--line)' },
  fexp: { padding: '9px 12px', background: 'var(--bg-1)', borderBottom: '1px solid var(--line)' },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 9 },
  // ── rule cards ──
  rule: { border: '1px solid var(--bd-1, var(--line))', borderRadius: 12, marginBottom: 12, background: 'var(--bg-2)', overflow: 'hidden' },
  ruleHead: { display: 'flex', alignItems: 'center', gap: 9, padding: '10px 13px', background: 'rgba(255,255,255,.025)', borderBottom: '1px solid var(--line)' },
  rtag: { fontFamily: 'ui-monospace, monospace', fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', background: 'var(--bg-1)', border: '1px solid var(--line)', padding: '3px 8px', borderRadius: 6 },
  ruleBody: { padding: 13 },
  when: { display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', fontSize: 13, color: 'var(--fg-3)' },
  whenRow: { display: 'flex', alignItems: 'center', gap: 8, rowGap: 8, flexWrap: 'wrap', fontSize: 13, color: 'var(--fg-3)', padding: '10px 13px', borderBottom: '1px solid var(--line)', background: 'rgba(255,255,255,.012)' },
  // ── formula ──
  eq: { fontFamily: 'ui-monospace, monospace', fontSize: 13, color: 'var(--fg-3)', marginRight: 2, whiteSpace: 'nowrap' },
  exprRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
  exprStack: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 },
  stackLine: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '4px 0' },
  term: { display: 'inline-flex', alignItems: 'center', gap: 7, minHeight: 34, padding: '2px 11px', borderRadius: 9, background: 'var(--bg-1)', border: '1px solid var(--line)' },
  termX: { color: 'var(--fg-4)', fontFamily: 'ui-monospace, monospace', fontSize: 14 },
  onum: { width: 22, textAlign: 'center', fontWeight: 700, color: GREEN, fontFamily: 'ui-monospace, monospace', fontSize: 12 },
  opBtn: { height: 30, minWidth: 40, padding: '0 8px', background: 'var(--bg-0)', border: '1px solid var(--line)', borderRadius: 8, color: 'var(--fg-1)', fontWeight: 700, fontSize: 15, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'ui-monospace, monospace' },
  paren: (on) => ({ cursor: 'pointer', fontFamily: 'ui-monospace, monospace', fontSize: 13, fontWeight: 600, padding: '0 1px', userSelect: 'none', color: on ? GREEN : 'var(--fg-4)', opacity: on ? 1 : 0.22 }),
  add: { minHeight: 34, display: 'inline-flex', alignItems: 'center', padding: '0 13px', borderRadius: 9, border: '1px dashed var(--bd-1, var(--line))', color: 'var(--fg-4)', fontSize: 12.5, background: 'transparent', cursor: 'pointer' },
  preview: { marginTop: 12, padding: '9px 12px', borderRadius: 8, background: 'var(--bg-0)', border: '1px solid var(--line)', fontFamily: 'ui-monospace, monospace', fontSize: 12, color: 'var(--fg-3)', lineHeight: 1.6, wordBreak: 'break-all' },
  res: { color: GREEN, fontWeight: 600 },
  // ── map table ──
  maptbl: { margin: '8px 0 2px 6px', border: '1px dashed #c79bff', borderRadius: 9, overflow: 'hidden', background: 'rgba(199,155,255,.05)', maxWidth: 360 },
  mh: { display: 'flex', justifyContent: 'space-between', padding: '7px 12px', borderBottom: '1px solid var(--line)', fontFamily: 'ui-monospace, monospace', fontSize: 10.5, color: '#c79bff', letterSpacing: '.3px' },
  kvrow: { display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 7, padding: '5px 12px' },
  // ── shared ──
  ix: { background: 'none', border: 'none', color: 'var(--fg-3)', cursor: 'pointer', fontSize: 14, padding: '2px 6px', borderRadius: 6, lineHeight: 1 },
  factorTrig: (kind) => ({ height: 28, border: 'none', background: 'transparent', padding: '0 4px', fontFamily: 'ui-monospace, monospace', fontWeight: 600, fontSize: 13, color: kind === 'const' ? 'var(--fg-1)' : kind === 'map' ? TYPE_COLOR.map : GREEN }),
  numIn: { border: 'none', background: 'transparent', width: 100, color: 'var(--fg-0)', fontFamily: 'ui-monospace, monospace', fontSize: 13 },
};

const clone = (x) => JSON.parse(JSON.stringify(x));
const fmtRes = (n) => (typeof n === 'number' && isFinite(n) ? n.toFixed(6).replace(/\.?0+$/, (m) => (m.includes('.') ? '' : m)) : '—');

export const DynamicRulesEditor = ({ index }) => {
  const { watch, setValue } = useFormContext();
  const path = `endpoints.${index}.dynamicRules`;
  const raw = watch(path);
  // DYNAMIC 的「model =」下拉取自「模型」tab 声明的 servedModels(模型与计费解耦)。
  const servedRaw = watch(`endpoints.${index}.servedModel`) || '';
  const servedModels = String(servedRaw).split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
  const U = raw && Array.isArray(raw.rules) && raw.rules.some((r) => Array.isArray(r.items)) ? raw : null;

  useEffect(() => {
    if (!U) setValue(path, raw ? deserializeRules(raw) : EMPTY_DYNAMIC_UI(), { shouldDirty: !!raw });
  }, [U]); // eslint-disable-line react-hooks/exhaustive-deps

  const [validation, setValidation] = useState({ ok: true, errors: [], loading: false });
  const [templates, setTemplates] = useState([]);
  const [layout, setLayout] = useState('stack'); // 默认竖排堆叠(更清爽);可切横排
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonDraft, setJsonDraft] = useState('');
  const [jsonErr, setJsonErr] = useState('');
  const [jsonExpanded, setJsonExpanded] = useState(false);
  const timerRef = useRef(null);
  const reqRef = useRef(0);

  useEffect(() => {
    let alive = true;
    XApi.apiSvc.getDynamicTemplates().then((list) => alive && setTemplates(Array.isArray(list) ? list : [])).catch(() => {});
    return () => { alive = false; };
  }, []);

  const cfgKey = U ? JSON.stringify(compileRules(U)) : '';
  useEffect(() => {
    if (!U) return;
    setValidation((v) => ({ ...v, loading: true }));
    if (timerRef.current) clearTimeout(timerRef.current);
    const myReq = ++reqRef.current;
    timerRef.current = setTimeout(async () => {
      try {
        const res = await XApi.apiSvc.validateDynamicRules({ dynamicRules: JSON.parse(cfgKey) });
        if (reqRef.current === myReq) setValidation({ ok: !!res?.ok, errors: res?.errors || [], loading: false });
      } catch (e) {
        if (reqRef.current === myReq) setValidation({ ok: false, errors: [{ message: e?.message || '校验请求失败' }], loading: false });
      }
    }, 400);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [cfgKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!U) return <div style={{ color: 'var(--fg-3)', fontSize: 13, padding: 8 }}>初始化动态计费配置…</div>;

  const update = (mutator) => { const draft = clone(U); mutator(draft); setValue(path, draft, { shouldDirty: true }); };
  const numNames = numFieldsOf(U);
  const mapNames = mapFieldsOf(U);

  const applyTemplate = (key) => {
    const tpl = templates.find((t) => t.key === key);
    if (tpl && tpl.spec) setValue(path, deserializeRules(clone(tpl.spec)), { shouldDirty: true });
  };

  // JSON 直写:进入时把当前配置 dump 成 JSON;编辑时解析回 UI-state(失败只记错、不动配置)。
  const enterJson = () => { setJsonDraft(JSON.stringify(compileRules(U), null, 2)); setJsonErr(''); setJsonMode(true); };
  const onJsonChange = (text) => {
    setJsonDraft(text);
    try {
      const parsed = JSON.parse(text);
      setJsonErr('');
      setValue(path, deserializeRules(parsed), { shouldDirty: true });
    } catch (e) {
      setJsonErr(e?.message || 'JSON 解析失败');
    }
  };
  // Syntax-highlighted JSON editor (lazy CodeEditor; plain textarea while it loads).
  const renderJsonEditor = (minHeight) => (
    <React.Suspense
      fallback={
        <textarea className="mono" spellCheck={false} value={jsonDraft} onChange={(e) => onJsonChange(e.target.value)}
          style={{ width: '100%', minHeight, boxSizing: 'border-box', background: 'var(--bg-0)', border: '1px solid var(--line)', borderRadius: 9, padding: 11, color: '#cdd6e6', fontFamily: 'ui-monospace, monospace', fontSize: 12.5, resize: 'vertical' }} />
      }
    >
      <div data-color-mode="dark" style={{ border: `1px solid ${jsonErr ? 'var(--err, #ff7a7a)' : 'var(--line)'}`, borderRadius: 9, overflow: 'auto', maxHeight: minHeight > 400 ? '72vh' : 'none' }}>
        <CodeEditor value={jsonDraft} language="json" onChange={(e) => onJsonChange(e.target.value)} padding={12}
          placeholder={'{ "version": 2, "fields": [], "rules": [] }'}
          style={{ minHeight, background: 'var(--bg-0)', fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }} />
      </div>
    </React.Suspense>
  );

  // field handlers
  const setField = (i, k, v) => update((d) => { d.fields[i][k] = v; });
  const addField = () => update((d) => { d.fields.push(newField()); });
  const delField = (i) => update((d) => { d.fields.splice(i, 1); });
  // rule handlers
  const addRule = () => update((d) => { d.rules.push(newRule(numNames)); });
  const delRule = (ri) => update((d) => { d.rules.splice(ri, 1); });
  const moveRule = (ri, dir) => update((d) => { const j = ri + dir; if (j >= 0 && j < d.rules.length) [d.rules[ri], d.rules[j]] = [d.rules[j], d.rules[ri]]; });
  const toggleElse = (ri, on) => update((d) => {
    d.rules[ri].isElse = on;
    if (!on && !(d.rules[ri].conds || []).length) d.rules[ri].conds = [{ field: '', op: '=', value: '' }];
  });
  // 复合条件(AND):一条规则可有多条件,全部成立才命中。
  const setCond = (ri, ci, k, v) => update((d) => { d.rules[ri].conds[ci][k] = v; });
  const addCond = (ri) => update((d) => { (d.rules[ri].conds || (d.rules[ri].conds = [])).push({ field: '', op: '=', value: '' }); });
  const delCond = (ri, ci) => update((d) => { if (d.rules[ri].conds.length > 1) d.rules[ri].conds.splice(ci, 1); });
  // item / factor handlers
  const addItem = (ri) => update((d) => { d.rules[ri].items.push(newItem(numNames)); });
  const delItem = (ri, ii) => update((d) => { if (d.rules[ri].items.length > 1) d.rules[ri].items.splice(ii, 1); });
  const setItemOp = (ri, ii, v) => update((d) => { d.rules[ri].items[ii].op = v; });
  const toggleParen = (ri, ii, which) => update((d) => { const it = d.rules[ri].items[ii]; it[which] = !it[which]; });
  // 简化单元模型:每个单元 = 主体(固定值 / 数值字段 / map字段)+(数值字段时)可选「× 单价」。
  // 单元内不堆多因子;a × b 用两个单元 + 单元间的 × 运算符表达。
  const setUnitPrimary = (ri, ii, v) => update((d) => {
    const it = d.rules[ri].items[ii];
    const prevConst = it.factors[1] && it.factors[1].kind === 'const' ? it.factors[1].value
      : it.factors[0] && it.factors[0].kind === 'const' ? it.factors[0].value : null;
    if (v === '__const__') it.factors = [{ kind: 'const', value: prevConst != null ? prevConst : 0 }];
    else if (v.slice(0, 2) === 'n:') {
      const field = v.slice(2);
      it.factors = prevConst != null && prevConst !== '' ? [{ kind: 'number', field }, { kind: 'const', value: prevConst }] : [{ kind: 'number', field }];
    } else {
      const field = v.slice(2);
      const keep = it.factors[0] && it.factors[0].kind === 'map' && it.factors[0].field === field ? it.factors[0] : null;
      it.factors = [keep || { kind: 'map', field, options: [{ key: '', price: 0 }], onUnmatched: { mode: 'reject', price: 0 }, _open: true }];
    }
  });
  const setUnitConst = (ri, ii, val) => update((d) => { const f = d.rules[ri].items[ii].factors[0]; if (f && f.kind === 'const') f.value = val; });
  const setUnitPrice = (ri, ii, val) => update((d) => {
    const it = d.rules[ri].items[ii];
    if (!it.factors[0] || it.factors[0].kind !== 'number') return;
    if (val === '' || val == null) it.factors = [it.factors[0]];
    else if (it.factors[1] && it.factors[1].kind === 'const') it.factors[1].value = val;
    else it.factors = [it.factors[0], { kind: 'const', value: val }];
  });
  const toggleMapOpen = (ri, ii) => update((d) => { const f = d.rules[ri].items[ii].factors[0]; if (f && f.kind === 'map') f._open = !f._open; });
  const setFactor = (ri, ii, fi, k, v) => update((d) => {
    const f = d.rules[ri].items[ii].factors[fi];
    if (k === 'unmatchedMode') { f.onUnmatched.mode = v; return; }
    if (k === 'unmatchedPrice') { f.onUnmatched.price = v; return; }
    f[k] = v;
  });
  const addMap = (ri, ii, fi) => update((d) => { d.rules[ri].items[ii].factors[fi].options.push({ key: '', price: 0 }); });
  const delMap = (ri, ii, fi, oi) => update((d) => { d.rules[ri].items[ii].factors[fi].options.splice(oi, 1); });
  const setMap = (ri, ii, fi, oi, k, v) => update((d) => { d.rules[ri].items[ii].factors[fi].options[oi][k] = v; });
  // combine override (advanced escape hatch)
  const setOverride = (ri, v) => update((d) => { d.rules[ri].combineOverride = v; });
  const resetOverride = (ri) => update((d) => { d.rules[ri].combineOverride = ''; });
  const setAdv = (k, v) => update((d) => { d[k] = v; });

  // 单元主体三选一:固定值(const)/ 数值字段(n:)/ map 字段(m:)。类型由 value 前缀编码,
  // 标签只放字段名;选中后 number 显绿、map 显紫 + 「查表」药丸。
  const primaryOptions = [{ value: '__const__', label: '固定值' }]
    .concat(numNames.map((n) => ({ value: 'n:' + n, label: n })))
    .concat(mapNames.map((n) => ({ value: 'm:' + n, label: n })));
  const ROOT_OPTS = ROOTS.map((r) => ({ value: r, label: ROOTLABEL[r] }));
  const TYPE_OPTS = FIELD_TYPES.map((t) => ({ value: t.v, label: t.l }));
  const OP_OPTS = OPS.map((o) => ({ value: o, label: o }));
  const ITEM_OP_OPTS = [{ value: '+', label: '+' }, { value: '-', label: '−' }, { value: '*', label: '×' }, { value: '/', label: '÷' }];

  // ── 一个单元(计费项):主体(固定值 / 数值字段 / map)+(数值字段时)可选「× 单价」 ──
  const renderUnit = (ri, ii, it, canDel) => {
    const f0 = it.factors[0] || { kind: 'const', value: 0 };
    const f1 = it.factors[1];
    const primarySel = f0.kind === 'const' ? '__const__' : (f0.kind === 'number' ? 'n:' + f0.field : 'm:' + f0.field);
    return (
      <span style={S.term}>
        <Select value={primarySel} onChange={(v) => setUnitPrimary(ri, ii, v)} options={primaryOptions} triggerStyle={S.factorTrig(f0.kind)} />
        {f0.kind === 'const' && (
          <input className="mono" style={S.numIn} placeholder="0.01" value={f0.value ?? ''} onChange={(e) => setUnitConst(ri, ii, e.target.value)} />
        )}
        {f0.kind === 'number' && (
          <>
            <span style={S.termX}>×</span>
            <input className="mono" style={{ ...S.numIn, width: 116 }} placeholder="单价(空=不乘)" value={f1 && f1.kind === 'const' ? (f1.value ?? '') : ''} onChange={(e) => setUnitPrice(ri, ii, e.target.value)} />
          </>
        )}
        {f0.kind === 'map' && (
          <button type="button"
            style={{ fontSize: 11, color: TYPE_COLOR.map, background: 'rgba(199,155,255,.1)', border: '1px solid rgba(199,155,255,.3)', borderRadius: 6, padding: '1px 7px', cursor: 'pointer' }}
            title="编辑 值 → 价 表" onClick={() => toggleMapOpen(ri, ii)}>查表</button>
        )}
        {canDel && (
          <button type="button" style={{ ...S.ix, marginLeft: 2, opacity: 0.55, fontSize: 12 }} title="删除此单元" onClick={() => delItem(ri, ii)}>
            <XIcons.IconTrash size={11} />
          </button>
        )}
      </span>
    );
  };

  const renderMapStrip = (ri, ii, fi, f) => (
    <div style={S.maptbl}>
      <div style={S.mh}><span>{f.field || '(选 map 字段)'} 取值</span><span>单价</span></div>
      {f.options.map((o, oi) => (
        <div style={S.kvrow} key={oi}>
          <input className="input" placeholder="值,如 1080p" value={o.key ?? ''} onChange={(e) => setMap(ri, ii, fi, oi, 'key', e.target.value)} />
          <input className="input mono" placeholder="0.008" value={o.price ?? ''} onChange={(e) => setMap(ri, ii, fi, oi, 'price', e.target.value)} />
          <button type="button" style={S.ix} onClick={() => delMap(ri, ii, fi, oi)}>✕</button>
        </div>
      ))}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 12px', borderTop: '1px dashed var(--line)' }}>
        <button type="button" style={{ ...S.ghost, padding: '3px 9px', color: GREEN }} onClick={() => addMap(ri, ii, fi)}>+ 加一档</button>
        <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>未命中</span>
        <Select value={f.onUnmatched.mode} onChange={(v) => setFactor(ri, ii, fi, 'unmatchedMode', v)} width={92}
          options={[{ value: 'reject', label: '拒绝' }, { value: 'default', label: '默认价' }]} />
        {f.onUnmatched.mode === 'default' && (
          <input className="input mono" style={{ width: 78 }} placeholder="默认价" value={f.onUnmatched.price ?? ''} onChange={(e) => setFactor(ri, ii, fi, 'unmatchedPrice', e.target.value)} />
        )}
        <button type="button" style={{ ...S.ghost, padding: '3px 9px', marginLeft: 'auto', color: 'var(--fg-3)' }} onClick={() => setFactor(ri, ii, fi, '_open', false)}>收起</button>
      </div>
    </div>
  );

  // map tables for a rule's units (map is always the unit's primary = factors[0])
  const renderMaps = (r, ri) =>
    r.items.map((it, ii) => {
      const f = it.factors[0];
      return f && f.kind === 'map' && (f._open || !f.options.length || f.options.some((o) => !o.key))
        ? <React.Fragment key={ii}>{renderMapStrip(ri, ii, 0, f)}</React.Fragment>
        : null;
    });

  const renderItemRow = (r, ri, it, ii) => (
    <React.Fragment key={ii}>
      {ii > 0 && (
        <Select value={it.op || '+'} onChange={(v) => setItemOp(ri, ii, v)} options={ITEM_OP_OPTS} triggerStyle={S.opBox} />
      )}
      <span style={S.paren(it.open)} title="左括号 (" onClick={() => toggleParen(ri, ii, 'open')}>(</span>
      {renderUnit(ri, ii, it, r.items.length > 1)}
      <span style={S.paren(it.close)} title="右括号 )" onClick={() => toggleParen(ri, ii, 'close')}>)</span>
      {/* 删项已移入单元内(renderUnit) */}
    </React.Fragment>
  );

  const renderRule = (r, ri) => {
    const ov = r.combineOverride && r.combineOverride.trim();
    return (
      <div style={S.rule} key={ri}>
        <div style={S.ruleHead}>
          <span style={{ ...S.rtag, ...(r.isElse ? { color: 'var(--fg-2)' } : null) }}>规则 {ri + 1}{r.isElse ? ' · 兜底' : ''}</span>
          {r.isElse && <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>其它情况(无条件,命中前面未匹配的全部请求)</span>}
          <span style={{ flex: 1 }} />
          <button type="button" style={S.ix} disabled={ri === 0} onClick={() => moveRule(ri, -1)}>↑</button>
          <button type="button" style={S.ix} disabled={ri === U.rules.length - 1} onClick={() => moveRule(ri, 1)}>↓</button>
          <button type="button" style={{ ...S.ghost, padding: '3px 9px', color: 'var(--fg-3)' }} onClick={() => toggleElse(ri, !r.isElse)}>{r.isElse ? '改为有条件' : '设为兜底'}</button>
          <button type="button" style={S.ix} onClick={() => delRule(ri)}>✕</button>
        </div>
        {!r.isElse && (
          <div style={S.whenRow}>
            <span style={{ color: 'var(--fg-3)', flexShrink: 0 }}>当</span>
            {(r.conds || []).map((c, ci) => (
              <React.Fragment key={ci}>
                {ci > 0 && <span style={{ color: 'var(--fg-3)', fontWeight: 600, margin: '0 2px' }}>且</span>}
                {/* 一个条件 = 一个原子组:字段/符号/值/删除不从中间断行,删除在组内 */}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' }}>
                  <Select value={c.field} onChange={(v) => setCond(ri, ci, 'field', v)} placeholder="选字段" width={118}
                    options={[{ value: '', label: '选字段' }].concat(U.fields.map((f) => ({ value: f.name, label: f.name })))} />
                  <Select value={c.op} onChange={(v) => setCond(ri, ci, 'op', v)} options={OP_OPTS} width={64} />
                  {c.field === 'model' && servedModels.length ? (
                    // 模型:自动从 model 字段提取候选(datalist 提示),但仍可随意输入
                    <>
                      <input className="input" list={`wm-${ri}-${ci}`} style={{ width: 128 }} placeholder="选/输入模型"
                        value={c.value} onChange={(e) => setCond(ri, ci, 'value', e.target.value)} />
                      <datalist id={`wm-${ri}-${ci}`}>
                        {servedModels.map((m) => <option key={m} value={m} />)}
                      </datalist>
                    </>
                  ) : (
                    // string 等普通字段:随意输入
                    <input className="input" style={{ width: 116 }} placeholder="值,如 gpt-4o" value={c.value} onChange={(e) => setCond(ri, ci, 'value', e.target.value)} />
                  )}
                  {(r.conds || []).length > 1 && (
                    <button type="button" style={{ ...S.ix, fontSize: 11, opacity: 0.6 }} title="删条件" onClick={() => delCond(ri, ci)}><XIcons.IconTrash size={11} /></button>
                  )}
                </span>
              </React.Fragment>
            ))}
            <button type="button" style={{ ...S.ghost, padding: '2px 9px', fontSize: 11.5, color: 'var(--accent, #6aa6ff)', flexShrink: 0 }} onClick={() => addCond(ri)}>+ 且</button>
            <span style={{ color: 'var(--fg-3)', flexShrink: 0 }}>时</span>
          </div>
        )}
        <div style={S.ruleBody}>
          <div style={layout === 'row' ? S.exprRow : S.exprStack}>
            <span style={S.eq}>收费 =</span>
            {layout === 'row'
              ? r.items.map((it, ii) => renderItemRow(r, ri, it, ii))
              : r.items.map((it, ii) => (
                  <div style={S.stackLine} key={ii}>
                    {ii > 0
                      ? <Select value={it.op || '+'} onChange={(v) => setItemOp(ri, ii, v)} options={ITEM_OP_OPTS} triggerStyle={S.opBox} />
                      : <span style={{ width: 42 }} />}
                    <span style={S.paren(it.open)} title="左括号 (" onClick={() => toggleParen(ri, ii, 'open')}>(</span>
                    <span style={S.onum}>o{ii + 1}</span>
                    {renderUnit(ri, ii, it, r.items.length > 1)}
                    <span style={S.paren(it.close)} title="右括号 )" onClick={() => toggleParen(ri, ii, 'close')}>)</span>
                    {/* 删项已移入单元内(renderUnit) */}
                  </div>
                ))}
            <button type="button" style={S.add} onClick={() => addItem(ri)}>+ 加项</button>
          </div>
          {renderMaps(r, ri)}
          {ov && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 9, fontSize: 12, color: 'var(--warn, #e8bb55)' }}>
              <span style={{ whiteSpace: 'nowrap' }}>组合(自定义):</span>
              <input className="input mono" style={{ flex: 1, minWidth: 140 }} value={r.combineOverride} onChange={(e) => setOverride(ri, e.target.value)} />
              <button type="button" style={S.ix} title="恢复跟随运算符" onClick={() => resetOverride(ri)}>↺</button>
            </div>
          )}
          <div style={S.preview}>
            <span style={{ color: 'var(--fg-2)', fontWeight: 600 }}>计算公式</span> · {previewStr(r)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="col gap-3">
      {/* header: validity + templates */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={S.pill(validation.ok ? 'ok' : 'bad')}>
          {validation.loading ? '校验中…' : validation.ok ? '配置合法' : `${validation.errors.length} 处需修正`}
        </span>
        <span style={{ flex: 1 }} />
        {/* 可视化 ↔ JSON 直写 切换 */}
        <div style={{ display: 'inline-flex', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
          {[{ k: false, l: '可视化' }, { k: true, l: 'JSON' }].map((o) => (
            <button key={String(o.k)} type="button" onClick={() => (o.k ? enterJson() : setJsonMode(false))}
              style={{ padding: '4px 11px', fontSize: 12, cursor: 'pointer', border: 'none', background: jsonMode === o.k ? 'var(--bg-2)' : 'transparent', color: jsonMode === o.k ? 'var(--fg-1)' : 'var(--fg-3)', fontWeight: jsonMode === o.k ? 600 : 400 }}>
              {o.l}
            </button>
          ))}
        </div>
        {!jsonMode && templates.length > 0 && (
          <Select value="" onChange={(v) => v && applyTemplate(v)} placeholder="套用模板…" width={170}
            options={templates.map((t) => ({ value: t.key, label: t.label || t.key }))} />
        )}
      </div>
      {!validation.ok && validation.errors.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {validation.errors.map((e, i) => (
            <li key={i} style={{ color: 'var(--err, #ff7a7a)', fontSize: 12.5, margin: '3px 0' }}>
              {(e.rule != null ? `规则${e.rule + 1}: ` : e.field ? `${e.field}: ` : '') + (e.message || String(e))}
            </li>
          ))}
        </ul>
      )}

      {jsonMode ? (
        <div style={S.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ ...S.hint, flex: 1 }}>
              直接编辑配置 JSON(DynamicRulesConfig v2);改完切回「可视化」即可。
              {jsonErr && <span style={{ color: 'var(--err, #ff7a7a)' }}> · {jsonErr}</span>}
            </span>
            <button type="button" style={S.ghost} title="弹窗放大编辑" onClick={() => setJsonExpanded(true)}>⤢ 放大</button>
          </div>
          {renderJsonEditor(340)}
        </div>
      ) : (
        <>

      {/* ① fields */}
      <div style={S.card}>
        <div style={S.secHead}>
          <span style={S.secNum}>1</span><span style={S.secTitle}>字段池 · Fields</span>
          <span style={{ ...S.hint, marginLeft: 4 }}>— 计费要读取的值</span>
          <span style={{ flex: 1 }} />
          <button type="button" style={S.ghost} onClick={addField}>+ 加字段</button>
        </div>
        {!U.fields.length ? (
          <p style={S.hint}>还没有字段。点「+ 加字段」声明计费要读的值。</p>
        ) : (
          <div style={S.tbl}>
            <div style={S.th}><span>字段名</span><span>来源</span><span>JSON 路径</span><span>类型</span><span /></div>
            {U.fields.map((f, i) => (
              <React.Fragment key={i}>
                <div style={S.tr}>
                  <input className="input mono" style={{ color: GREEN }} placeholder="字段名" value={f.name} onChange={(e) => setField(i, 'name', e.target.value)} />
                  <Select value={f.source} onChange={(v) => setField(i, 'source', v)} options={ROOT_OPTS} width="100%" />
                  <input className="input mono" placeholder="usage.input_tokens" value={f.path} onChange={(e) => setField(i, 'path', e.target.value)} />
                  <Select value={f.type} onChange={(v) => setField(i, 'type', v)} options={TYPE_OPTS} width="100%" triggerStyle={{ fontWeight: 700, color: TYPE_COLOR[f.type] }} />
                  <span style={{ display: 'inline-flex', justifyContent: 'flex-end' }}>
                    {f.type === 'number' && <button type="button" style={S.ix} title="预扣/区间" onClick={() => setField(i, '_open', !f._open)}>⚙</button>}
                    <button type="button" style={S.ix} onClick={() => delField(i)}>✕</button>
                  </span>
                </div>
                {f.type === 'number' && f._open && (
                  <div style={S.fexp}>
                    <div style={S.grid3}>
                      <div>
                        <label style={S.hint}>预扣上限 estimatedMax <span style={{ color: 'var(--fg-4)' }}>{isResp(f.source) ? '选填,缺省按平台上限预扣;模板已给值' : '(响应字段才用到)'}</span></label>
                        <input className="input mono" value={f.estMax ?? ''} onChange={(e) => setField(i, 'estMax', e.target.value)} />
                      </div>
                      {isResp(f.source) && U.holdPolicy === 'typical' && (
                        <div><label style={S.hint}>典型值 estimatedTypical *</label><input className="input mono" value={f.estTyp ?? ''} onChange={(e) => setField(i, 'estTyp', e.target.value)} /></div>
                      )}
                      <div><label style={S.hint}>缺省值 default</label><input className="input mono" value={f.default ?? ''} onChange={(e) => setField(i, 'default', e.target.value)} /></div>
                      <div><label style={S.hint}>下限 clamp.min</label><input className="input mono" value={f.clampMin ?? ''} onChange={(e) => setField(i, 'clampMin', e.target.value)} /></div>
                      <div><label style={S.hint}>上限 clamp.max</label><input className="input mono" value={f.clampMax ?? ''} onChange={(e) => setField(i, 'clampMax', e.target.value)} /></div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* ② rules */}
      <div style={S.card}>
        <div style={S.secHead}>
          <span style={S.secNum}>2</span><span style={S.secTitle}>计费规则 · Rules</span>
          <span style={{ ...S.hint, marginLeft: 4 }}>— 从上往下,第一条命中的生效;末条可省条件作兜底</span>
          <span style={{ flex: 1 }} />
          {/* 横排 ↔ 竖排堆叠 切换 */}
          <div style={{ display: 'inline-flex', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden', marginRight: 8 }}>
            {[{ k: 'row', l: '横排' }, { k: 'stack', l: '竖排' }].map((o) => (
              <button key={o.k} type="button" onClick={() => setLayout(o.k)}
                style={{ padding: '4px 11px', fontSize: 12, cursor: 'pointer', border: 'none', background: layout === o.k ? 'var(--bg-2)' : 'transparent', color: layout === o.k ? 'var(--fg-1)' : 'var(--fg-3)', fontWeight: layout === o.k ? 600 : 400 }}>
                {o.l}
              </button>
            ))}
          </div>
          <button type="button" style={S.ghost} onClick={addRule}>+ 加规则</button>
        </div>
        {U.rules.map((r, ri) => renderRule(r, ri))}
      </div>

      {/* advanced */}
      <details>
        <summary style={{ cursor: 'pointer', color: 'var(--fg-3)', fontSize: 12 }}>高级:预扣策略 / 最低·封顶</summary>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10, alignItems: 'end' }}>
          <div style={{ maxWidth: 200 }}>
            <label style={S.hint}>预扣策略 holdPolicy</label>
            <Select value={U.holdPolicy || 'max'} onChange={(v) => setAdv('holdPolicy', v)} width="100%"
              options={[{ value: 'max', label: 'max(安全上限)' }, { value: 'typical', label: 'typical(典型值)' }]} />
          </div>
          <div style={{ maxWidth: 130 }}><label style={S.hint}>最低收费 minCost</label><input className="input mono" placeholder="可空" value={U.minCost ?? ''} onChange={(e) => setAdv('minCost', e.target.value)} /></div>
          <div style={{ maxWidth: 130 }}><label style={S.hint}>封顶 maxCost</label><input className="input mono" placeholder="可空" value={U.maxCost ?? ''} onChange={(e) => setAdv('maxCost', e.target.value)} /></div>
        </div>
      </details>
        </>
      )}
      {jsonExpanded && (
        <Dialog open onClose={() => setJsonExpanded(false)} maxWidth={920} responsive>
          <DialogHeader title="编辑配置 JSON · DynamicRulesConfig v2" onClose={() => setJsonExpanded(false)} />
          <DialogBody padding={16}>
            {jsonErr && <div style={{ color: 'var(--err, #ff7a7a)', fontSize: 12.5, marginBottom: 8 }}>{jsonErr}</div>}
            {renderJsonEditor(560)}
          </DialogBody>
        </Dialog>
      )}
    </div>
  );
};
